import { useState, useEffect, useRef } from 'react'

export interface VisitorData {
  // User-provided data
  name?: string
  role?: string
  firm?: string

  // Session data
  sessionId: string
  firstVisit: string
  lastVisit: string
  visitCount: number
  totalTimeSpent: number // in seconds
  currentSessionStart: string
  currentSessionTime: number

  // Location data (from IP)
  ipAddress?: string
  city?: string
  region?: string
  country?: string
  timezone?: string

  // Device & Browser data
  userAgent: string
  browser: string
  os: string
  device: string
  screenResolution: string
  language: string

  // Behavior data
  pagesVisited: string[]
  sectionsViewed: string[]
  chatInteractions: number
  generalQuestions: number
  trungQuestions: number
  filesUploaded: number

  // Referral data
  referrer: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
}

// Generate unique session ID
const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Parse user agent to get browser, OS, device info
const parseUserAgent = (ua: string) => {
  const browser = /Edge\/\d+/.test(ua)
    ? 'Edge'
    : /Chrome\/\d+/.test(ua)
      ? 'Chrome'
      : /Firefox\/\d+/.test(ua)
        ? 'Firefox'
        : /Safari\/\d+/.test(ua) && !/Chrome/.test(ua)
          ? 'Safari'
          : 'Other'

  const os = /Windows/.test(ua)
    ? 'Windows'
    : /Mac OS X/.test(ua)
      ? 'macOS'
      : /Linux/.test(ua)
        ? 'Linux'
        : /Android/.test(ua)
          ? 'Android'
          : /iOS|iPhone|iPad/.test(ua)
            ? 'iOS'
            : 'Other'

  const device = /Mobile|Android|iPhone/.test(ua)
    ? 'Mobile'
    : /Tablet|iPad/.test(ua)
      ? 'Tablet'
      : 'Desktop'

  return { browser, os, device }
}

// Get location data from IP (using free ipapi.co service)
const fetchLocationData = async (): Promise<{
  ipAddress?: string
  city?: string
  region?: string
  country?: string
  timezone?: string
}> => {
  try {
    const response = await fetch('https://ipapi.co/json/')
    if (!response.ok) throw new Error('Failed to fetch location')

    const data = await response.json()
    return {
      ipAddress: data.ip,
      city: data.city,
      region: data.region,
      country: data.country_name,
      timezone: data.timezone,
    }
  } catch (error) {
    console.warn('Could not fetch location data:', error)
    return {}
  }
}

// Parse UTM parameters from URL
const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search)
  return {
    utmSource: params.get('utm_source') || undefined,
    utmMedium: params.get('utm_medium') || undefined,
    utmCampaign: params.get('utm_campaign') || undefined,
  }
}

export const useVisitorAnalytics = () => {
  const [visitorData, setVisitorData] = useState<VisitorData | null>(null)
  const sessionStartTime = useRef<number>(Date.now())
  const timeUpdateInterval = useRef<NodeJS.Timeout>()
  const isInitialized = useRef(false)

  // Initialize visitor data
  useEffect(() => {
    if (isInitialized.current) return
    isInitialized.current = true

    const initializeAnalytics = async () => {
      // Get existing data from localStorage
      const storedData = localStorage.getItem('visitorAnalytics')
      const existingData: Partial<VisitorData> = storedData
        ? JSON.parse(storedData)
        : {}

      // Get user-provided data from WelcomeIntro
      const userData = localStorage.getItem('visitorData')
      const userInfo = userData ? JSON.parse(userData) : {}

      // Parse user agent
      const ua = navigator.userAgent
      const { browser, os, device } = parseUserAgent(ua)

      // Get screen resolution
      const screenResolution = `${window.screen.width}x${window.screen.height}`

      // Get referrer
      const referrer = document.referrer || 'Direct'

      // Get UTM parameters
      const utmParams = getUtmParams()

      // Fetch location data (async)
      const locationData = await fetchLocationData()

      // Create new session ID
      const sessionId = generateSessionId()
      const now = new Date().toISOString()

      // Merge all data
      const newData: VisitorData = {
        // User-provided data
        name: userInfo.name || existingData.name,
        role: userInfo.role || existingData.role,
        firm: userInfo.firm || existingData.firm,

        // Session data
        sessionId,
        firstVisit: existingData.firstVisit || now,
        lastVisit: now,
        visitCount: (existingData.visitCount || 0) + 1,
        totalTimeSpent: existingData.totalTimeSpent || 0,
        currentSessionStart: now,
        currentSessionTime: 0,

        // Location data
        ipAddress: locationData.ipAddress || existingData.ipAddress,
        city: locationData.city || existingData.city,
        region: locationData.region || existingData.region,
        country: locationData.country || existingData.country,
        timezone: locationData.timezone || existingData.timezone,

        // Device & Browser data
        userAgent: ua,
        browser,
        os,
        device,
        screenResolution,
        language: navigator.language,

        // Behavior data
        pagesVisited: existingData.pagesVisited || [],
        sectionsViewed: existingData.sectionsViewed || [],
        chatInteractions: existingData.chatInteractions || 0,
        generalQuestions: parseInt(
          localStorage.getItem('general_inquiry_count') || '0',
        ),
        trungQuestions: parseInt(
          localStorage.getItem('trung_inquiry_count') || '0',
        ),
        filesUploaded: existingData.filesUploaded || 0,

        // Referral data
        referrer,
        utmSource: utmParams.utmSource || existingData.utmSource,
        utmMedium: utmParams.utmMedium || existingData.utmMedium,
        utmCampaign: utmParams.utmCampaign || existingData.utmCampaign,
      }

      setVisitorData(newData)
      localStorage.setItem('visitorAnalytics', JSON.stringify(newData))
    }

    initializeAnalytics()
  }, [])

  // Update time spent every 5 seconds
  useEffect(() => {
    if (!visitorData) return

    timeUpdateInterval.current = setInterval(() => {
      const currentTime = Math.floor(
        (Date.now() - sessionStartTime.current) / 1000,
      )

      setVisitorData((prev) => {
        if (!prev) return prev

        const updated = {
          ...prev,
          currentSessionTime: currentTime,
          totalTimeSpent: (prev.totalTimeSpent || 0) + 5,
        }

        localStorage.setItem('visitorAnalytics', JSON.stringify(updated))
        return updated
      })
    }, 5000) // Update every 5 seconds

    return () => {
      if (timeUpdateInterval.current) {
        clearInterval(timeUpdateInterval.current)
      }
    }
  }, [visitorData])

  // Track page visibility (pause timer when tab is hidden)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Pause timer
        if (timeUpdateInterval.current) {
          clearInterval(timeUpdateInterval.current)
        }
      } else {
        // Resume timer
        sessionStartTime.current = Date.now()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  // Track page navigation
  const trackPageVisit = (pageName: string) => {
    setVisitorData((prev) => {
      if (!prev) return prev

      const pagesVisited = [...prev.pagesVisited]
      if (!pagesVisited.includes(pageName)) {
        pagesVisited.push(pageName)
      }

      const updated = { ...prev, pagesVisited }
      localStorage.setItem('visitorAnalytics', JSON.stringify(updated))
      return updated
    })
  }

  // Track section view (for scroll tracking)
  const trackSectionView = (sectionName: string) => {
    setVisitorData((prev) => {
      if (!prev) return prev

      const sectionsViewed = [...prev.sectionsViewed]
      if (!sectionsViewed.includes(sectionName)) {
        sectionsViewed.push(sectionName)
      }

      const updated = { ...prev, sectionsViewed }
      localStorage.setItem('visitorAnalytics', JSON.stringify(updated))
      return updated
    })
  }

  // Track chat interaction
  const trackChatInteraction = () => {
    setVisitorData((prev) => {
      if (!prev) return prev

      const updated = {
        ...prev,
        chatInteractions: prev.chatInteractions + 1,
      }

      localStorage.setItem('visitorAnalytics', JSON.stringify(updated))
      return updated
    })
  }

  // Track file upload
  const trackFileUpload = () => {
    setVisitorData((prev) => {
      if (!prev) return prev

      const updated = {
        ...prev,
        filesUploaded: prev.filesUploaded + 1,
      }

      localStorage.setItem('visitorAnalytics', JSON.stringify(updated))
      return updated
    })
  }

  // Update user info when WelcomeIntro is completed
  const updateUserInfo = (name: string, role: string, firm: string) => {
    setVisitorData((prev) => {
      if (!prev) return prev

      const updated = {
        ...prev,
        name,
        role,
        firm,
      }

      localStorage.setItem('visitorAnalytics', JSON.stringify(updated))
      return updated
    })
  }

  // Export data (for admin/debugging)
  const exportAnalytics = (): string => {
    return JSON.stringify(visitorData, null, 2)
  }

  // Send analytics to server (optional - you'd need a backend endpoint)
  const sendAnalyticsToServer = async (endpoint: string) => {
    if (!visitorData) return

    try {
      await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(visitorData),
      })
    } catch (error) {
      console.error('Failed to send analytics:', error)
    }
  }

  return {
    visitorData,
    trackPageVisit,
    trackSectionView,
    trackChatInteraction,
    trackFileUpload,
    updateUserInfo,
    exportAnalytics,
    sendAnalyticsToServer,
  }
}
